// src/ui/mobile-scroll.ts
//
// iOS Safari can be unreliable about engaging native `overflow-y: auto`
// on a position:fixed/absolute child even when the CSS is technically
// correct (a known mix of quirks with body locking, viewport units, and
// nested scroll containers). This helper guarantees scroll on the
// `.control-panel` by intercepting touchmove and directly updating
// `scrollTop`. If native scroll IS working, this just supplements it
// (the same `scrollTop += dy` produces the same visual result).
//
// Touches that start on form controls — slider, button, select, label,
// the input inside a slider row, etc. — are passed through untouched
// so the controls keep their native behaviour (drag thumb, tap button,
// open dropdown).

const TARGET_SELECTOR = '.control-panel';

const isFormControl = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'BUTTON' ||
    tag === 'SELECT' ||
    tag === 'OPTION' ||
    tag === 'TEXTAREA' ||
    tag === 'LABEL'
  );
};

interface DragState {
  panel: HTMLElement;
  lastY: number;
  passthrough: boolean;
}

let state: DragState | null = null;

const onTouchStart = (e: TouchEvent) => {
  if (e.touches.length !== 1) return;
  const target = e.target as Element | null;
  if (!target) return;
  const panel = target.closest(TARGET_SELECTOR) as HTMLElement | null;
  if (!panel) return;
  state = {
    panel,
    lastY: e.touches[0]!.clientY,
    passthrough: isFormControl(target),
  };
};

const onTouchMove = (e: TouchEvent) => {
  if (!state) return;
  if (state.passthrough) return;
  if (e.touches.length !== 1) return;
  const y = e.touches[0]!.clientY;
  const dy = state.lastY - y;
  state.panel.scrollTop += dy;
  state.lastY = y;
  e.preventDefault();
};

const onTouchEnd = () => {
  state = null;
};

export function installMobileScroll(): void {
  if (typeof window === 'undefined') return;
  if (!('ontouchstart' in window) && !(navigator.maxTouchPoints > 0)) return;
  document.addEventListener('touchstart', onTouchStart, { passive: true });
  // touchmove must be non-passive so we can preventDefault and stop iOS
  // from interpreting the swipe as anything else.
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('touchend', onTouchEnd, { passive: true });
  document.addEventListener('touchcancel', onTouchEnd, { passive: true });
}
